<template>
  <InParens :visible="outerParens">
    <template v-if="isCondNot(cond)">
      <button
        class="btn-logic"
        @click="swapLogic"
        @mouseenter="hoverNot = true"
        @mouseleave="hoverNot = false"
      >
        Not
      </button>

      <ConditionEdit
        :modelValue="cond.Not"
        @update:modelValue="onEditHead"
        :parentLogic="condType"
      />
    </template>

    <template v-else-if="isCondAnd(cond) || isCondOr(cond)">
      <ConditionEdit
        v-if="inner.length > 0"
        :modelValue="inner[0]"
        @update:modelValue="onEditHead"
        :parentLogic="condType"
      />

      <template v-if="inner.length > 1">
        <button
          class="btn-logic"
          @click="swapLogic"
          @mouseenter="hoverAndOr = true"
          @mouseleave="hoverAndOr = false"
        >
          {{ isCondAnd(cond) ? "And" : "Or" }}
        </button>

        <InParens :visible="innerParens">
          <ConditionEdit
            :modelValue="cutils.wrap(cond, inner.slice(1))"
            @update:modelValue="onEditTail"
            :parentLogic="condType"
          />
        </InParens>
      </template>
    </template>

    <template v-else>{{ assertUnreachable(cond) }}</template>
  </InParens>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { assertUnreachable, isDefined } from "@/utils";
import { Condition } from "@/keyboard";
import {
  ConditionLogic,
  ConditionTypeLogic,
  isCondNot,
  isCondAnd,
  isCondOr,
  conditionTypeLogic,
} from "@/keyboard/leds";
import * as cutils from "@/keyboard/condition_utils";
import ConditionEdit from "@/components/leds/ConditionEdit.vue";
import InParens from "@/components/leds/InParens.vue";

const props = defineProps<{
  modelValue: ConditionLogic;
  parentLogic?: ConditionTypeLogic;
}>();

const emit = defineEmits<{
  (_e: "update:modelValue", _value?: Condition): void;
}>();

// TODO: pass hover info to ConditionTypeButton and apply some animation
const hoverNot = ref(false);
const hoverAndOr = ref(false);

// NOTE: And/Or contain a list of conditions, this treats it in functional
// way by rendering only the head and passing the tail recursively.

// Some computed variables to avoid code repetition in template
const cond = computed(() => props.modelValue);
const inner = computed(() => cutils.inner(cond.value));
const condType = computed(() => conditionTypeLogic(cond.value));
const outerParens = computed(() =>
  cutils.outerParens(condType.value, props.parentLogic)
);
const innerParens = computed(
  // () => !isCondNot(cond.value) && isOther(cond.value, inner.value[1])
  // TODO: Inner parens are unnecessary? Verify?
  () => false
);

const onEditHead = (c?: Condition) => {
  const v = cond.value;
  if (isCondNot(v))
    //throw Error("onEditHead with Not");
    return emit("update:modelValue", c == undefined ? undefined : { Not: c });

  // console.log("edit head", v, c);

  const conds = [c, ...cutils.unwrap(v).slice(1)].filter(isDefined);
  emit(
    "update:modelValue",
    conds.length == 0 ? undefined : cutils.cleanup(cutils.wrap(v, conds))
  );
};

const onEditTail = (c?: Condition) => {
  const v = cond.value;
  if (isCondNot(v)) throw Error("onEditTail with Not");

  // console.log("edit tail", v, c);

  const head = cutils.wrap(v, [cutils.unwrap(v)[0]]);
  emit("update:modelValue", cutils.cleanup(cutils.merge(head, c)));
};

const swapLogic = () => {
  const v = cond.value;

  // Clicking on Not just removes it
  if (isCondNot(v)) return emit("update:modelValue", v.Not);

  // Because And/Or may have multiple conditions underneath we need to swap logic
  // only for the first pair of conditions (only it is rendered by this component).
  const inner = cutils.unwrap(v);
  const head = cutils.wrap(v, inner.slice(0, 1));
  const tail = cutils.wrap(v, inner.slice(1));
  const merged = cutils.merge(cutils.swap(head), cutils.cleanup(tail));

  emit("update:modelValue", cutils.cleanup(merged));
};
</script>

<style scoped>
.btn-logic {
  @apply btn btn-sm btn-primary;
}
</style>
